import { LocaleResources } from 'locales';
import { ParsedUrlQuery } from 'querystring';
import { MIDDLE_CATEGORY_QUERY_PARAM, SMALL_CATEGORY_QUERY_PARAM } from 'src/constants/category';
import { BrandSchema } from 'src/models/brand/schema';
import { Category } from 'src/stores/Category';

export const useBrandPageMetaData = (
  category: Category,
  query: ParsedUrlQuery,
  localeResources: LocaleResources,
  brandName: string
): { title: string; description: string } => {
  const { title, titleWithParams, description, descriptionWithParams } = localeResources.brands.meta;
  const wamazingShopTitle = localeResources.commons.wamazingShop;

  const getMetaData = (
    queryParamCategoryKey: typeof MIDDLE_CATEGORY_QUERY_PARAM | typeof SMALL_CATEGORY_QUERY_PARAM
  ) => {
    const categories =
      queryParamCategoryKey === MIDDLE_CATEGORY_QUERY_PARAM ? category.middleCategories : category.smallCategories;
    const queryParamCategoryValue = query[queryParamCategoryKey];
    if (typeof queryParamCategoryValue === 'string') {
      const queryParamCategoryName = categories.find(
        (category) => category.slug === query[queryParamCategoryKey]
      )?.name;
      return {
        title: queryParamCategoryName
          ? `${titleWithParams(brandName, queryParamCategoryName)} | ${wamazingShopTitle}`
          : `${title(brandName)} | ${wamazingShopTitle}`,
        description: queryParamCategoryName
          ? descriptionWithParams(brandName, queryParamCategoryName)
          : description(brandName),
      };
    }
  };

  const middleCategoryMetaData = getMetaData(MIDDLE_CATEGORY_QUERY_PARAM);
  if (middleCategoryMetaData) return middleCategoryMetaData;

  const smallCategoryMetaData = getMetaData(SMALL_CATEGORY_QUERY_PARAM);
  if (smallCategoryMetaData) return smallCategoryMetaData;

  return {
    title: `${title(brandName)} | ${wamazingShopTitle}`,
    description: description(brandName),
  };
};

export const useCategoryPageMetaData = (
  query: ParsedUrlQuery,
  brands: BrandSchema[],
  categoryName: string,
  localeResources: LocaleResources
) => {
  const { page } = query;
  const wamazingShopTitle = localeResources.commons.wamazingShop;
  const { titleWithParams, descriptionWithParams } = localeResources.brands.meta;
  const { title, description } = localeResources.category.meta;
  const queryParamBrandKey = 'brand';
  const queryParamBrandValue = query[queryParamBrandKey];
  if (typeof queryParamBrandValue === 'string') {
    const queryParamBrandName = brands.find((brand) => brand.slug === queryParamBrandValue)?.name;
    return {
      title: queryParamBrandName
        ? `${titleWithParams(queryParamBrandName, categoryName)} | ${wamazingShopTitle}`
        : `${title(categoryName)} | ${wamazingShopTitle}`,
      description: queryParamBrandName
        ? descriptionWithParams(queryParamBrandName, categoryName)
        : description(categoryName),
    };
  }
  return {
    title: `${title(categoryName)} | ${wamazingShopTitle}${page ? ` - page: ${page}` : ''}`,
    description: description(categoryName),
  };
};
